import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOpeningBalance, saveOpeningBalance, deleteOpeningBalanceRow } from '../../services/api';
import { FiSave, FiClock, FiTrash2 } from 'react-icons/fi';
import Toast from '../../components/Toast';

const BUCKETS = [
  { key: 'usd_cash', currency: '$', label: 'USD Cash' },
  { key: 'usd_bank', currency: '$', label: 'USD Bank' },
  { key: 'k_cash',   currency: 'K', label: 'Kwacha Cash' },
  { key: 'k_bank',   currency: 'K', label: 'Kwacha Bank' },
  { key: 'k_momo',   currency: 'K', label: 'Kwacha Mobile Money' },
];

const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const OpeningBalance = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    usd_cash: '', usd_bank: '', k_cash: '', k_bank: '', k_momo: '',
    as_of_date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const fetchHistory = () => {
    getOpeningBalance(true).then(r => {
      const rows = Array.isArray(r.data) ? r.data : [];
      setHistory(rows);
      // Pre-fill the form with the most recent row so admins see what's
      // currently active and can adjust from there.
      if (rows[0]) {
        setForm(p => ({
          ...p,
          usd_cash: rows[0].usd_cash || '',
          usd_bank: rows[0].usd_bank || '',
          k_cash:   rows[0].k_cash   || '',
          k_bank:   rows[0].k_bank   || '',
          k_momo:   rows[0].k_momo   || '',
        }));
      }
    }).catch(() => {});
  };
  useEffect(() => { fetchHistory(); }, []);

  const totalUSD = (parseFloat(form.usd_cash || 0) || 0) + (parseFloat(form.usd_bank || 0) || 0);
  const totalK   = (parseFloat(form.k_cash   || 0) || 0) + (parseFloat(form.k_bank   || 0) || 0) + (parseFloat(form.k_momo || 0) || 0);

  const handleSave = async () => {
    if (!form.as_of_date) return showToast('As-of date is required', 'error');
    setSaving(true);
    try {
      await saveOpeningBalance({
        usd_cash: parseFloat(form.usd_cash || 0) || 0,
        usd_bank: parseFloat(form.usd_bank || 0) || 0,
        k_cash:   parseFloat(form.k_cash   || 0) || 0,
        k_bank:   parseFloat(form.k_bank   || 0) || 0,
        k_momo:   parseFloat(form.k_momo   || 0) || 0,
        as_of_date: form.as_of_date,
        notes: form.notes || null,
      });
      showToast(t('openingBalance.saved', { defaultValue: 'Opening balance saved' }));
      fetchHistory();
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed', 'error');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('openingBalance.confirmDelete', { defaultValue: 'Delete this opening-balance history row?' }))) return;
    try {
      await deleteOpeningBalanceRow(id);
      showToast('Deleted', 'error');
      fetchHistory();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
  };

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1>{t('openingBalance.title', { defaultValue: 'Cash Book Opening Balance' })}</h1>
          <p>{t('openingBalance.subtitle', { defaultValue: 'Set how much money you started with in each of the 5 drawers. The Cash Book uses this as its starting balance.' })}</p>
        </div>
      </div>

      <div className="table-card" style={{ padding: 20, marginBottom: 20 }}>
        <div className="form-grid">
          <div className="form-group">
            <label>{t('common.date')} <span className="required">*</span></label>
            <input type="date" value={form.as_of_date} onChange={set('as_of_date')} />
            <small style={{ color: '#9ca3af', fontSize: 11 }}>
              {t('openingBalance.asOfHint', { defaultValue: 'Cash Book uses the latest saved opening row whose date is on or before the report start.' })}
            </small>
          </div>
        </div>

        {/* USD section */}
        <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong style={{ fontSize: 14, color: '#16a34a' }}>USD ($)</strong>
            <strong style={{ fontSize: 14, color: '#16a34a' }}>${fmt(totalUSD)}</strong>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('openingBalance.cash', { defaultValue: 'Cash' })} ($)</label>
              <input type="number" step="0.01" value={form.usd_cash} onChange={set('usd_cash')} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>{t('openingBalance.bank', { defaultValue: 'Bank' })} ($)</label>
              <input type="number" step="0.01" value={form.usd_bank} onChange={set('usd_bank')} placeholder="0.00" />
            </div>
          </div>
        </div>

        {/* Kwacha section */}
        <div style={{ marginTop: 16, padding: 16, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong style={{ fontSize: 14, color: '#2563eb' }}>Kwacha (K)</strong>
            <strong style={{ fontSize: 14, color: '#2563eb' }}>K{fmt(totalK)}</strong>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('openingBalance.cash', { defaultValue: 'Cash' })} (K)</label>
              <input type="number" step="0.01" value={form.k_cash} onChange={set('k_cash')} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>{t('openingBalance.bank', { defaultValue: 'Bank' })} (K)</label>
              <input type="number" step="0.01" value={form.k_bank} onChange={set('k_bank')} placeholder="0.00" />
            </div>
            <div className="form-group">
              <label>{t('openingBalance.momo', { defaultValue: 'Mobile Money' })} (K)</label>
              <input type="number" step="0.01" value={form.k_momo} onChange={set('k_momo')} placeholder="0.00" />
            </div>
          </div>
        </div>

        <div className="form-grid" style={{ marginTop: 16 }}>
          <div className="form-group form-full">
            <label>{t('common.notes', { defaultValue: 'Notes' })}</label>
            <input value={form.notes} onChange={set('notes')} placeholder="Optional" />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <FiSave /> {saving ? t('common.saving') : t('openingBalance.save', { defaultValue: 'Save Opening Balance' })}
          </button>
        </div>
      </div>

      {history.length > 1 && (
        <div className="table-card">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8, color: '#6b7280', fontSize: 13, fontWeight: 600 }}>
            <FiClock /> {t('openingBalance.history', { defaultValue: 'History (older saves are kept for audit)' })}
          </div>
          <table>
            <thead>
              <tr>
                <th>{t('common.date')}</th>
                {BUCKETS.map(b => <th key={b.key}>{b.currency} {b.label.replace('USD ', '').replace('Kwacha ', '')}</th>)}
                <th>{t('table.by')}</th>
                <th>{t('common.notes', { defaultValue: 'Notes' })}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {history.map((row, i) => (
                <tr key={row.id} style={{ background: i === 0 ? '#f0fdf4' : 'transparent' }}>
                  <td>
                    <strong>{row.as_of_date}</strong>
                    {i === 0 && <span className="pill pill-green" style={{ marginLeft: 6, fontSize: 10 }}>{t('common.active', { defaultValue: 'Active' })}</span>}
                  </td>
                  {BUCKETS.map(b => (
                    <td key={b.key}>{b.currency}{fmt(row[b.key])}</td>
                  ))}
                  <td>{row.set_by_name || '—'}</td>
                  <td>{row.notes || '—'}</td>
                  <td>
                    <button className="btn-icon danger" onClick={() => handleDelete(row.id)} title={t('common.delete', { defaultValue: 'Delete' })}>
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default OpeningBalance;
